/**
 * lib/vouchers.ts
 * ------------------------------------------------------------------
 * PAYMENT VOUCHERS — the instruction to move money out of the school.
 *
 * PURE. A voucher and an actor in, an answer out. No Firestore, no React.
 *
 * SEPARATION OF DUTIES IS THE WHOLE DESIGN, and it is a rule about PEOPLE
 * rather than about roles:
 *
 *     preparer   →   checker   →   approver
 *     (raises)       (verifies)    (authorises)
 *
 * Three different human beings. Somebody will eventually hold two of those
 * capabilities for a perfectly good reason — a small office, somebody on
 * leave — and the distinct-actor guard is what still holds when they do.
 * Checking a permission alone would let a bursar with two hats raise, check
 * and approve their own payment, which is the exact arrangement every
 * embezzlement case in a school has in common.
 *
 * THE GUARD IS HERE, NOT IN THE BUTTONS. Hiding an action from a screen is a
 * courtesy to whoever is looking at it. This module is what the write path
 * and the Cloud Function both call, so a request that never went near the
 * screen is refused the same way.
 *
 * TWO BANK SNAPSHOTS, AND THIS IS THE CONTROL THAT MATTERS MOST. What the
 * approver authorised is recorded when they authorise it. What is about to
 * be paid is read again at payment time. If those differ, the voucher stops:
 * somebody changed the destination between authorisation and payment, which
 * is precisely the shape of supplier fraud. A warning banner over a snapshot
 * frozen before the change would say nothing, because the change happened
 * afterwards.
 *
 * EVENT-SOURCED. Every transition is an append-only event; `currentStatus`
 * is the denormalised view lists and rules read. A voucher's history is the
 * evidence, and a status field that can be overwritten is not evidence.
 * ------------------------------------------------------------------ */
import { round2 } from '@/lib/financeCore';
import type { SupplierBank } from '@/lib/suppliers';
import { bankDetailsDiffer } from '@/lib/suppliers';

export const VOUCHER_COLLECTIONS = {
  vouchers: 'financeVouchers',
  /** Events live in the subcollection `financeVouchers/{id}/events`. */
  eventsSub: 'events',
  counters: 'financeCounters',
} as const;

export const voucherCounterId = (fy: string) => `vouchers-${fy}`;

export function formatVoucherNo(fy: string, seq: number): string {
  return `PV/${fy.replace(/^FY/, '')}/${String(seq).padStart(4, '0')}`;
}

/* ================================ types ================================ */

export type VoucherStatus =
  | 'draft'
  | 'submitted'
  | 'checked'
  | 'approved'
  | 'paid'
  | 'rejected'
  | 'cancelled';

export const VOUCHER_STATUS_LABEL: Record<VoucherStatus, string> = {
  draft: 'Draft',
  submitted: 'Awaiting check',
  checked: 'Awaiting approval',
  approved: 'Approved — to pay',
  paid: 'Paid',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

/** Nothing moves out of these. */
export const VOUCHER_TERMINAL: VoucherStatus[] = ['paid', 'rejected', 'cancelled'];

export type VoucherSourceType =
  | 'adhoc'
  | 'supplier_invoice'
  | 'purchase_order'
  | 'recurring'
  | 'refund'
  | 'petty_cash'
  | 'payroll'
  | 'statutory';

export const VOUCHER_SOURCE_LABEL: Record<VoucherSourceType, string> = {
  adhoc: 'Ad-hoc payment',
  supplier_invoice: 'Supplier invoice',
  purchase_order: 'Against a purchase order',
  recurring: 'Recurring bill',
  refund: 'Refund',
  petty_cash: 'Petty cash',
  payroll: 'Payroll — net salaries',
  statutory: 'Statutory remittance',
};

/**
 * Sources a screen emits rather than a person keying.
 *
 * The payroll run raises its own vouchers, so the voucher editor must not
 * offer them: editing one here would put it out of step with the payslips
 * behind it, and the payslips are the truth.
 */
export const AUTO_EMITTED_SOURCES: VoucherSourceType[] = ['payroll', 'statutory'];

export const isAutoEmitted = (t?: VoucherSourceType): boolean =>
  !!t && AUTO_EMITTED_SOURCES.includes(t);

export type PaymentMethodOut = 'eft' | 'cheque' | 'cash';

export const PAYMENT_METHOD_OUT_LABEL: Record<PaymentMethodOut, string> = {
  eft: 'EFT / bank transfer',
  cheque: 'Cheque',
  cash: 'Cash',
};

export interface VoucherLine {
  description: string;
  /** GL account code. Captured at entry — retro-coding several hundred
   *  vouchers later is miserable, and an uncoded voucher cannot be posted
   *  without a human re-reading what it was for. */
  accountCode: string;
  amount: number;
}

/** The payee's banking, frozen at a moment. */
export interface PayeeSnapshot {
  supplierId?: string;
  staffId?: string;
  name: string;
  bank?: SupplierBank;
  /** Whether the register said these details were verified AT THAT MOMENT. */
  bankVerified?: boolean;
  snappedAt: string;
}

export interface Voucher {
  id: string;
  voucherNo: string;
  fy: string;
  source: { type: VoucherSourceType; id?: string };
  payeeName: string;
  supplierId?: string;
  staffId?: string;
  /** What the approver authorised. Written when they approve. */
  payeeAtApproval?: PayeeSnapshot;
  /** What is about to be paid. Read again at payment time. */
  payeeAtPayment?: PayeeSnapshot;
  lines: VoucherLine[];
  total: number;
  narration: string;
  /**
   * THE SUPPLIER'S OWN INVOICE NUMBER, which is the only reference they
   * can match. It earns its place twice: a remittance advice without it
   * says "we paid you for something", and it is a far stronger duplicate
   * check than payee-and-amount — the same invoice number twice is a
   * duplicate, where the same amount twice is often a weekly charge.
   */
  supplierInvoiceNo?: string;
  /** The order this settles, denormalised from source for display. */
  poNo?: string;
  dueDate?: string;
  paymentRef: string;
  method: PaymentMethodOut;
  currentStatus: VoucherStatus;
  preparedBy: string;
  preparedByName: string;
  preparedAt: string;
  checkedBy?: string;
  checkedByName?: string;
  checkedAt?: string;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: string;
  paidAt?: string;
  bankRef?: string;
  rejectedReason?: string;
  attachments?: { path: string; label: string }[];
  autoEmitted?: boolean;
  /**
   * Does approving this raise the payable, or was it raised already?
   *
   * true (default) — Dr the line accounts, Cr the payable. Suppliers, ad-hoc.
   * false          — payroll and statutory, where the payslip journal has
   *                  ALREADY credited NET_SALARIES_PAYABLE and PAYE_PAYABLE.
   *                  Accruing again would double the entire payroll.
   */
  raisesAccrual?: boolean;
  /** System key of the payable this settles. AP_TRADE for trade creditors. */
  payableAccountKey?: string;
  accrualJournalId?: string;
  paymentJournalId?: string;
  createdAt: string;
  updatedAt: string;
}

export type VoucherAction = 'submit' | 'check' | 'approve' | 'reject' | 'cancel' | 'pay';

export interface VoucherEvent {
  id: string;
  action: VoucherAction;
  from: VoucherStatus;
  to: VoucherStatus;
  by: string;
  byName: string;
  at: string;
  note?: string;
}

/* ========================== who may do what ========================== */

export interface VoucherActor {
  uid: string;
  name?: string;
  canRaise: boolean;
  canCheck: boolean;
  canApprove: boolean;
  canPay: boolean;
}

export const distinctFromPreparer = (v: Voucher, a: VoucherActor): boolean =>
  !v.preparedBy || v.preparedBy !== a.uid;

export const distinctFromChecker = (v: Voucher, a: VoucherActor): boolean =>
  !v.checkedBy || v.checkedBy !== a.uid;

/**
 * Why this actor cannot take this action, or null if they can.
 *
 * A SENTENCE, NOT A BOOLEAN. "You cannot approve this" leaves somebody
 * clicking; "you raised this voucher, so somebody else has to approve it"
 * ends the question. The same string is what the Cloud Function returns, so
 * a refusal reads identically wherever it is met.
 */
export function voucherActionProblem(
  v: Voucher,
  action: VoucherAction,
  actor: VoucherActor,
): string | null {
  if (VOUCHER_TERMINAL.includes(v.currentStatus) && action !== 'cancel') {
    return `This voucher is ${VOUCHER_STATUS_LABEL[v.currentStatus].toLowerCase()}. Nothing more can be done to it.`;
  }

  switch (action) {
    case 'submit':
      if (v.currentStatus !== 'draft') return 'Only a draft can be submitted.';
      if (!actor.canRaise) return 'You cannot raise payment vouchers.';
      return linesProblem(v);

    case 'check':
      if (v.currentStatus !== 'submitted') return 'This voucher is not waiting to be checked.';
      if (!actor.canCheck) return 'You cannot check payment vouchers.';
      /* THE GUARD THAT HOLDS WHEN SOMEBODY WEARS TWO HATS. */
      if (!distinctFromPreparer(v, actor)) {
        return 'You raised this voucher, so somebody else has to check it.';
      }
      return null;

    case 'approve':
      if (v.currentStatus !== 'checked') return 'This voucher has not been checked yet.';
      if (!actor.canApprove) return 'You cannot approve payment vouchers.';
      if (!distinctFromPreparer(v, actor)) {
        return 'You raised this voucher, so somebody else has to approve it.';
      }
      if (!distinctFromChecker(v, actor)) {
        return 'You checked this voucher, so somebody else has to approve it.';
      }
      return null;

    case 'pay':
      if (v.currentStatus !== 'approved') return 'Only an approved voucher can be paid.';
      if (!actor.canPay) return 'You cannot release payments.';
      return paymentProblem(v);

    case 'reject':
      if (!['submitted', 'checked'].includes(v.currentStatus)) {
        return 'Only a voucher awaiting check or approval can be rejected.';
      }
      if (!actor.canCheck && !actor.canApprove) return 'You cannot reject payment vouchers.';
      return null;

    case 'cancel':
      if (v.currentStatus === 'paid') return 'A paid voucher cannot be cancelled. Reverse the payment instead.';
      if (VOUCHER_TERMINAL.includes(v.currentStatus)) return 'This voucher is already closed.';
      // The person who raised it may withdraw it; otherwise it takes authority.
      if (!distinctFromPreparer(v, actor) || actor.canApprove) return null;
      return 'Only the person who raised this, or an approver, can cancel it.';

    default:
      return 'Unknown action.';
  }
}

/** The actions this actor could actually take right now. */
export function availableActions(v: Voucher, actor: VoucherActor): VoucherAction[] {
  return (['submit', 'check', 'approve', 'pay', 'reject', 'cancel'] as VoucherAction[])
    .filter((a) => voucherActionProblem(v, a, actor) === null);
}

/* ============================ the contents ============================ */

/** Why this voucher is not fit to be submitted, or null if it is. */
export function linesProblem(v: Pick<Voucher, 'lines' | 'total' | 'payeeName' | 'narration'>): string | null {
  if (!String(v.payeeName ?? '').trim()) return 'Say who is being paid.';
  if (!String(v.narration ?? '').trim()) return 'Say what the payment is for.';
  const lines = (v.lines ?? []).filter((l) => Number(l.amount) !== 0);
  if (!lines.length) return 'A voucher needs at least one line.';
  if (lines.some((l) => !String(l.description ?? '').trim())) return 'Every line needs a description.';
  /* An uncoded line cannot be posted without somebody re-reading the
     voucher months later and guessing what it was for. */
  if (lines.some((l) => !String(l.accountCode ?? '').trim())) {
    return 'Every line needs an account, so the payment can be posted without guesswork.';
  }
  if (lines.some((l) => Number(l.amount) < 0)) {
    return 'A line cannot be negative. A reduction is a credit note, not a negative payment.';
  }
  const sum = round2(lines.reduce((a, l) => a + (Number(l.amount) || 0), 0));
  if (sum !== round2(Number(v.total) || 0)) {
    return `The lines come to ${sum.toFixed(2)} but the voucher says ${round2(Number(v.total) || 0).toFixed(2)}.`;
  }
  if (sum <= 0) return 'A voucher must be for more than nothing.';
  return null;
}

/* ===================== the bank-change control ===================== */

/**
 * Have the banking details moved between authorisation and payment?
 *
 * THE CONTROL THAT CATCHES SUPPLIER FRAUD. The realistic attack is not
 * forging an approval — it is letting a genuine voucher be approved and then
 * changing where the money goes before it leaves. Both snapshots exist so
 * that question can be asked at the last moment, which is the only moment
 * the answer is worth anything.
 */
export function bankChangedSinceApproval(v: Voucher): boolean {
  if (!v.payeeAtApproval || !v.payeeAtPayment) return false;
  return bankDetailsDiffer(v.payeeAtApproval.bank, v.payeeAtPayment.bank);
}

/**
 * Why this voucher cannot be paid, or null if it can.
 *
 * Separate from the permission check because these are refusals about the
 * MONEY rather than about the person: an approver with every right in the
 * system still may not release a payment into an account that changed after
 * they authorised it.
 */
export function paymentProblem(v: Voucher): string | null {
  if (bankChangedSinceApproval(v)) {
    return 'The payee’s bank details changed after this was approved. It cannot be paid until '
      + 'somebody re-approves it against the new details.';
  }
  if (v.method === 'eft') {
    const bank = v.payeeAtPayment?.bank ?? v.payeeAtApproval?.bank;
    if (!bank?.accountNumber) {
      return 'There are no bank details for this payee, so an EFT cannot be exported.';
    }
    if (v.payeeAtPayment && v.payeeAtPayment.bankVerified === false) {
      return 'Nobody has verified this payee’s bank details. Confirm them on the supplier '
        + 'record before releasing the payment.';
    }
  }
  return null;
}

/* ============================== reporting ============================== */

/** Approved and unpaid — what the school owes and has agreed to pay. */
export function outstandingPayables(vouchers: Voucher[]): Voucher[] {
  return vouchers.filter((v) => v.currentStatus === 'approved');
}

/**
 * What the AP_TRADE control account should equal.
 *
 * Only vouchers that RAISED the payable count. A payroll voucher settles a
 * liability the payslip journal already created, so counting it here would
 * double the entire payroll — the same trap the posting engine guards.
 */
export function tradeCreditorsTotal(vouchers: Voucher[]): number {
  return round2(vouchers
    .filter((v) => v.currentStatus === 'approved')
    .filter((v) => v.raisesAccrual !== false)
    .filter((v) => (v.payableAccountKey ?? 'AP_TRADE') === 'AP_TRADE')
    .reduce((a, v) => a + (Number(v.total) || 0), 0));
}

export interface AgingBucket {
  label: string;
  from: number;
  to: number | null;
  vouchers: Voucher[];
  total: number;
}

/**
 * How overdue the school's own bills are.
 *
 * An undated bill is treated as due today rather than as never due: a
 * voucher nobody dated is not a voucher nobody has to pay, and bucketing it
 * as "not yet due" is how a supplier ends up chasing a school that thinks it
 * is up to date.
 */
export function payablesAging(vouchers: Voucher[], asOf: Date = new Date()): AgingBucket[] {
  const today = asOf.toISOString().slice(0, 10);
  const buckets: AgingBucket[] = [
    { label: 'Not yet due', from: -Infinity, to: 0, vouchers: [], total: 0 },
    { label: '1–30 days', from: 1, to: 30, vouchers: [], total: 0 },
    { label: '31–60 days', from: 31, to: 60, vouchers: [], total: 0 },
    { label: '61–90 days', from: 61, to: 90, vouchers: [], total: 0 },
    { label: 'Over 90 days', from: 91, to: null, vouchers: [], total: 0 },
  ];

  for (const v of outstandingPayables(vouchers)) {
    const due = v.dueDate ? v.dueDate.slice(0, 10) : today;
    const days = Math.round((Date.parse(today) - Date.parse(due)) / 86400000);
    const b = buckets.find((x) => days >= x.from && (x.to === null || days <= x.to))
      ?? buckets[buckets.length - 1];
    b.vouchers.push(v);
    b.total = round2(b.total + (Number(v.total) || 0));
  }
  return buckets;
}

/** A short, human sentence about where a voucher has got to. */
export function voucherStatusSummary(v: Voucher): string {
  switch (v.currentStatus) {
    case 'draft': return `Drafted by ${v.preparedByName}`;
    case 'submitted': return `Raised by ${v.preparedByName}, waiting to be checked`;
    case 'checked': return `Checked by ${v.checkedByName ?? 'someone'}, waiting for approval`;
    case 'approved': return `Approved by ${v.approvedByName ?? 'someone'} — ready to pay`;
    case 'paid': return `Paid${v.paidAt ? ` on ${v.paidAt.slice(0, 10)}` : ''}`;
    case 'rejected': return `Rejected${v.rejectedReason ? `: ${v.rejectedReason}` : ''}`;
    case 'cancelled': return 'Cancelled';
    default: return '';
  }
}

/* ========================== creditors reporting ========================== */

/**
 * WHO the school owes, not just how much.
 *
 * A creditors total answers the auditor. It does not answer the bursar,
 * whose question on a Monday is which supplier is owed what and how long
 * they have been waiting — and a supplier who has been waiting 90 days is
 * a different problem from ten who have been waiting a week, even when
 * the two totals match.
 *
 * KEYED BY supplierId WHERE THERE IS ONE, and by payee name otherwise. Not
 * every voucher goes to a registered supplier: a one-off reimbursement has
 * a name and no register entry, and dropping those would understate what
 * the school owes. Grouping them by name is imperfect — two spellings make
 * two creditors — which is exactly the visible nudge towards registering
 * a supplier properly.
 */
export interface CreditorSummary {
  key: string;
  supplierId?: string;
  name: string;
  /** Approved and unpaid, newest first. */
  vouchers: Voucher[];
  total: number;
  /** Days overdue of the oldest unpaid bill. Negative means not yet due. */
  oldestDays: number;
  buckets: { label: string; total: number }[];
}

const AGING_LABELS = ['Not yet due', '1–30 days', '31–60 days', '61–90 days', 'Over 90 days'];

/** Days a voucher is overdue as at `asOf`. Undated is treated as due today. */
export function daysOverdue(v: Voucher, asOf: Date = new Date()): number {
  const today = asOf.toISOString().slice(0, 10);
  const due = v.dueDate ? v.dueDate.slice(0, 10) : today;
  return Math.round((Date.parse(today) - Date.parse(due)) / 86400000);
}

function bucketFor(days: number): string {
  if (days <= 0) return AGING_LABELS[0];
  if (days <= 30) return AGING_LABELS[1];
  if (days <= 60) return AGING_LABELS[2];
  if (days <= 90) return AGING_LABELS[3];
  return AGING_LABELS[4];
}

/**
 * The creditors ledger, one row per supplier.
 *
 * Sorted by total owed, descending — the list is read to decide who gets
 * paid this week, and that decision starts at the top of the money.
 */
export function creditorsBySupplier(
  vouchers: Voucher[],
  asOf: Date = new Date(),
): CreditorSummary[] {
  const by = new Map<string, CreditorSummary>();

  for (const v of outstandingPayables(vouchers)) {
    const key = v.supplierId ?? `name:${(v.payeeName || 'Unnamed').trim().toLowerCase()}`;
    let row = by.get(key);
    if (!row) {
      row = {
        key,
        supplierId: v.supplierId,
        name: v.payeeName || 'Unnamed',
        vouchers: [],
        total: 0,
        oldestDays: -Infinity,
        buckets: AGING_LABELS.map((label) => ({ label, total: 0 })),
      };
      by.set(key, row);
    }
    const days = daysOverdue(v, asOf);
    row.vouchers.push(v);
    row.total = round2(row.total + (Number(v.total) || 0));
    row.oldestDays = Math.max(row.oldestDays, days);
    const b = row.buckets.find((x) => x.label === bucketFor(days))!;
    b.total = round2(b.total + (Number(v.total) || 0));
  }

  const rows = [...by.values()];
  for (const r of rows) {
    r.vouchers.sort((a, b) => (b.dueDate ?? '').localeCompare(a.dueDate ?? ''));
    if (r.oldestDays === -Infinity) r.oldestDays = 0;
  }
  return rows.sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
}

/**
 * Everything that happened with one supplier, oldest first.
 *
 * A STATEMENT IS NOT A LIST OF UNPAID BILLS. It is what the supplier's own
 * ledger should say, so it includes what was paid and when — that is how a
 * disagreement gets settled. Only vouchers that reached approval appear: a
 * draft or a rejected voucher is the school's internal business and was
 * never an obligation to anybody.
 *
 * The running balance is what the school still owed after each line, so the
 * closing figure is the balance, not a subtotal that needs interpreting.
 */
export interface SupplierStatementLine {
  voucher: Voucher;
  date: string;
  /** What was owed, on the day it became owed. */
  charged: number;
  /** What was settled, on the day it was settled. */
  paid: number;
  balance: number;
}

export function supplierStatement(
  vouchers: Voucher[],
  supplier: { id?: string; name: string },
): { lines: SupplierStatementLine[]; closing: number } {
  const key = supplier.id;
  const mine = vouchers.filter((v) => (key
    ? v.supplierId === key
    : !v.supplierId && (v.payeeName || '').trim().toLowerCase() === supplier.name.trim().toLowerCase()));

  const events: { date: string; charged: number; paid: number; voucher: Voucher }[] = [];
  for (const v of mine) {
    if (!v.approvedAt && v.currentStatus !== 'paid') continue;
    const amount = Number(v.total) || 0;
    events.push({
      date: (v.approvedAt ?? v.preparedAt ?? '').slice(0, 10),
      charged: amount,
      paid: 0,
      voucher: v,
    });
    if (v.currentStatus === 'paid') {
      events.push({ date: (v.paidAt ?? '').slice(0, 10), charged: 0, paid: amount, voucher: v });
    }
  }

  events.sort((a, b) => a.date.localeCompare(b.date)
    || a.voucher.voucherNo.localeCompare(b.voucher.voucherNo)
    || b.charged - a.charged);

  let balance = 0;
  const lines = events.map((e) => {
    balance = round2(balance + e.charged - e.paid);
    return { voucher: e.voucher, date: e.date, charged: e.charged, paid: e.paid, balance };
  });

  return { lines, closing: balance };
}
